import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';
import { VIEWPORT_MARGIN, placeVertically } from './floatingPlacement';

export type FloatingAlign = 'left' | 'right' | 'center';

export interface FloatingPosition {
    // Exactly one of top/bottom, and one of left/right, is set - the edge
    // closest to the trigger, so the menu grows away from it.
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    /** `align: 'center'` positions via `left` + `transform: translateX(-50%)` - true centering with no need to measure the menu's width. */
    centered: boolean;
    placement: 'top' | 'bottom';
    /** Room between the trigger and the viewport edge on the side it opens, so the menu can shrink to fit. */
    maxHeight: number;
    /** Only set when `matchTriggerWidth` is requested - lets a menu span exactly as wide as its trigger, e.g. a full-width "+ Link a patient" button. */
    width?: number;
}

interface UseFloatingPositionOptions {
    open: boolean;
    triggerRef: RefObject<HTMLElement>;
    align?: FloatingAlign;
    /** Open above the trigger whenever there's more (or equal) room above than below - not just as a last resort. */
    preferUp?: boolean;
    /** Used only to decide which side has "enough" room - the real rendered menu can be shorter or taller. */
    estimatedHeight?: number;
    /** Gap between the trigger and the menu, in px. */
    offset?: number;
    /** Force the menu's width to match the trigger's rendered width instead of sizing to its content. */
    matchTriggerWidth?: boolean;
}

const DEFAULT_ESTIMATED_HEIGHT = 320;

/**
 * Computes a `position: fixed` rect for a menu portaled to `document.body`,
 * anchored to a trigger - flips above when there isn't enough room below,
 * and recomputes on scroll (capture-phase, so any ancestor's scroll counts)
 * and resize. Pairs with FloatingMenu.tsx's `createPortal`.
 */
export function useFloatingPosition({
    open,
    triggerRef,
    align = 'left',
    preferUp = false,
    estimatedHeight = DEFAULT_ESTIMATED_HEIGHT,
    offset = 6,
    matchTriggerWidth = false
}: UseFloatingPositionOptions): FloatingPosition | null {
    const [position, setPosition] = useState<FloatingPosition | null>(null);

    const recompute = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const vertical = placeVertically({
            triggerTop: rect.top,
            triggerBottom: rect.bottom,
            viewportHeight: window.innerHeight,
            preferUp,
            estimatedHeight,
            offset
        });

        const horizontal =
            align === 'right'
                ? { right: Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.right) }
                : align === 'center'
                  ? { left: rect.left + rect.width / 2 }
                  : { left: Math.min(rect.left, window.innerWidth - VIEWPORT_MARGIN) };

        setPosition({
            ...vertical,
            ...horizontal,
            centered: align === 'center',
            width: matchTriggerWidth ? rect.width : undefined
        });
    }, [triggerRef, align, preferUp, estimatedHeight, offset, matchTriggerWidth]);

    useLayoutEffect(() => {
        if (!open) {
            setPosition(null);
            return;
        }
        recompute();
        window.addEventListener('scroll', recompute, true);
        window.addEventListener('resize', recompute);
        return () => {
            window.removeEventListener('scroll', recompute, true);
            window.removeEventListener('resize', recompute);
        };
    }, [open, recompute]);

    return position;
}
